import { useState, useEffect } from "react";
import { mutate } from "swr";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  getAdditionlNewnessFilteringOptions,
  getClearPaymentTypeModel,
  getCurrencyFromSetup,
  obtainsPaymentTypeOption,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import ModalBackground from "../../components/common/ModalBackground";
import AddOrEditPaymentType from "./components/AddOrEditPaymentType";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import localize from "../../common/localize";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import TrueFalseImage from "../../components/TrueFalseImage";
import {
  generateNoDataValues,
  getFiltersInfo,
  getValueInCurrency,
} from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function PaymentTypes() {
  const {
    paymentTypeModel,
    setPaymentTypeModel,
    paymentTypeData,
    PAYMENT_TYPE_ENDPOINT,
    config,
    pageIndexPaymentType,
    setPageIndexPaymentType,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [paymentTypeDataFiltered, setpaymentTypeDataFiltered] = useState(
    paymentTypeData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getAdditionlNewnessFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const paymentTypeDataObj = paymentTypeData.Data.find(
        (paymentTypeObj) => paymentTypeObj.Id === Number(id)
      );
      setPaymentTypeModel(paymentTypeDataObj);
    }
  }, [id]);

  useEffect(() => {
    setpaymentTypeDataFiltered(paymentTypeData.Data);
  }, [paymentTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setPaymentTypeModel(getClearPaymentTypeModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([PAYMENT_TYPE_ENDPOINT, config]);

    if (isDelete) {
      if (pageIndexPaymentType !== 0 && paymentTypeData.Data.length === 1) {
        setPageIndexPaymentType(pageIndexPaymentType - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${PAYMENT_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${PAYMENT_TYPE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${PAYMENT_TYPE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !paymentTypeModel?.Name || paymentTypeModel?.Name.length === 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.default_value,
      localize.need_observation,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.payment_type_title}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={PAYMENT_TYPE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="payment_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_payment_type}
          onClickToSubmit={() => {
            handleUpdateRequest(paymentTypeModel);
            setShowEditModal(false);
            setPaymentTypeModel(getClearPaymentTypeModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setPaymentTypeModel(getClearPaymentTypeModel());
          }}
          Component={
            <AddOrEditPaymentType
              model={paymentTypeModel}
              setModel={setPaymentTypeModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.payment_type_title}
            subtitle={localize.payment_type_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="payment_types_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_payment_type}
                onClickToSubmit={() => {
                  handleCreateRequest(paymentTypeModel);
                  setShowAddModal(false);
                  setPaymentTypeModel(getClearPaymentTypeModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setPaymentTypeModel(getClearPaymentTypeModel());
                }}
                Component={
                  <AddOrEditPaymentType
                    model={paymentTypeModel}
                    setModel={setPaymentTypeModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={paymentTypeModel.Name}
          entityType={localize.payment_type}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setPaymentTypeModel(getClearPaymentTypeModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(paymentTypeModel.Id);
            setShowDeleteModal(false);
            setPaymentTypeModel(getClearPaymentTypeModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexPaymentType}
            setPageIndex={setPageIndexPaymentType}
            dataArray={paymentTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          paymentTypeData.Data?.length !== 0
            ? paymentTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setPaymentTypeModel({
                          ...item,
                          TypeModel: {
                            Id: item.Type,
                            Name: obtainsPaymentTypeOption(item.Type)?.Name,
                          },
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setPaymentTypeModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  `${
                    item.Type === 0
                      ? getValueInCurrency(
                          getCurrencyFromSetup(),
                          item.DefaultValue
                        )
                      : item.DefaultValue
                  } ${item.Type === 1 ? " %" : ""}`,
                  <TrueFalseImage value={item.NeedObservation} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
